/**
 * Renders a `LessonBookingFailure` — the persistent in-step refusal notice
 * for the lesson-booking POST (this step does not use `butterToast` for
 * a failure; it renders it, with the recoveries `toLessonBookingFailure`
 * names). [BUT-843, ours]
 *
 * `role="alert"` and focus is moved to the notice on mount so a screen-reader
 * user who presses Confirm and gets refused actually hears why.
 *
 * @module pages/lessons/steps/components/LessonFailureNotice
 */

import { useEffect, useRef } from 'react';
import type { LessonBookingFailure, LessonBookingRecovery } from '../../lessonBookingErrors';

export interface LessonFailureNoticeProps {
  failure: LessonBookingFailure;
  onRecover: (recovery: LessonBookingRecovery) => void;
  /** Move focus here on mount. False for a notice present on arrival (not a response to an action). @default true */
  autoFocus?: boolean;
  /** @default 'lesson-booking-failure' */
  testId?: string;
}

export function LessonFailureNotice({
  failure,
  onRecover,
  autoFocus = true,
  testId = 'lesson-booking-failure',
}: LessonFailureNoticeProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
    // Only on mount — a fresh failure always focuses; re-renders with the
    // same failure object must not steal focus again.
  }, [autoFocus]);

  return (
    <div
      ref={ref}
      role="alert"
      tabIndex={-1}
      className="rounded-lg border border-red-200 bg-red-50 p-4"
      data-testid={testId}
    >
      <p className="text-sm text-red-800">{failure.message}</p>

      {failure.fieldErrors && failure.fieldErrors.length > 0 && (
        <ul className="mt-2 space-y-1">
          {failure.fieldErrors.map((fieldError) => (
            <li key={fieldError.field} className="text-sm text-red-800">
              {fieldError.message}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap gap-4">
        <button
          type="button"
          onClick={() => onRecover(failure.primary.recovery)}
          className="text-sm font-semibold text-red-800 underline text-interactive:text-red-900"
          data-testid="lesson-failure-primary"
        >
          {failure.primary.label}
        </button>
        {failure.secondary && (
          <button
            type="button"
            onClick={() => onRecover(failure.secondary!.recovery)}
            className="text-sm font-semibold text-red-800 underline text-interactive:text-red-900"
            data-testid="lesson-failure-secondary"
          >
            {failure.secondary.label}
          </button>
        )}
      </div>
    </div>
  );
}
